import { useNavigate } from "@tanstack/react-router";
import { SessionTypeIcon } from "@/modules/chat/components/SessionTypeIcon";
import { AgentsIcon } from "@/shared/icons";
import { formatHistoryTime } from "@/modules/chat/lib/format-history-time";
import type { CompanySession } from "./types";

interface Props {
  session: CompanySession;
  // The row is shared between the «awaiting» and «in_progress» columns.
  // The badge variant differs only by the data on the session (awaiting
  // wins), but the click target is identical: open the chat by sid.
}

export function CompanyLiveRow({ session }: Props) {
  const navigate = useNavigate();
  // The overview lists every company session, but only ones the admin can
  // actually read are click-through — opening a no-access session would
  // land on a 403 chat shell. Same pattern as the locked space cards.
  const clickable = session.accessible;
  const open = () => {
    if (!clickable) return;
    navigate({ to: "/", search: { sid: session.id } });
  };

  const agentName = session.agent?.label || session.agent?.name;

  return (
    <div
      className={`history-item${session.awaiting_user_input ? " history-item--awaiting" : ""}${clickable ? "" : " history-item--locked"}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      aria-disabled={clickable ? undefined : true}
      title={clickable ? undefined : "Нет доступа к содержимому этой сессии"}
      onClick={clickable ? open : undefined}
      onKeyDown={(e) => {
        if (clickable && e.key === "Enter") open();
      }}
    >
      <SessionTypeIcon
        kind={session.routine_id ? "routine" : "chat"}
        awaiting={session.awaiting_user_input}
        running={session.is_running}
        variant="badge"
        className="history-item-icon"
      />
      <div className="history-item-content">
        <div className="history-item-title-row">
          <span className="history-item-title">{session.title || "Новый чат"}</span>
          {session.space && (
            <span
              className="history-item-space"
              title={`Пространство: ${session.space.name}`}
            >
              {session.space.name}
            </span>
          )}
        </div>
        <div className="history-item-meta">
          <span>{formatHistoryTime(session.updated_at)}</span>
          {agentName && (
            <span className="history-item-meta-agent" title={`Агент: ${agentName}`}>
              <AgentsIcon className="history-item-bot-icon" />
              <span>{agentName}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
